import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, History, MailOpen, MousePointerClick, CalendarCheck, CalendarClock, Trash2 } from "lucide-react";
import { API, authHeaders } from "./shared";

const STATUS_STYLES = {
  scheduled: "border-brand-gold/40 text-brand-gold bg-brand-gold/10",
  sent: "border-green-500/40 text-green-400 bg-green-500/10",
  cancelled: "border-brand-border text-brand-text-muted",
  failed: "border-red-500/40 text-red-400 bg-red-500/10",
  blocked_safe_mode: "border-amber-500/40 text-amber-400 bg-amber-500/10",
};
const STATUS_LABELS = { scheduled: "Scheduled", sent: "Sent", cancelled: "Cancelled", failed: "Failed", blocked_safe_mode: "Blocked (Safe Mode)" };

const ScheduledSection = () => {
  const [items, setItems] = useState([]);
  const [reschedId, setReschedId] = useState(null);
  const [reschedWhen, setReschedWhen] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/scheduled-campaigns`, authHeaders())
      .then((r) => setItems(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  const cancel = async (c) => {
    if (!window.confirm(`Cancel the scheduled campaign "${c.campaign_name}"? It will not be sent.`)) return;
    try {
      await axios.delete(`${API}/admin/scheduled-campaigns/${c.id}`, authHeaders());
      toast.success("Campaign cancelled — nothing will be sent");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
  };

  const saveReschedule = async (c) => {
    if (!reschedWhen) { toast.error("Pick a new date and time"); return; }
    try {
      await axios.put(`${API}/admin/scheduled-campaigns/${c.id}`, { scheduled_for: new Date(reschedWhen).toISOString() }, authHeaders());
      toast.success(`Rescheduled to ${new Date(reschedWhen).toLocaleString()}`);
      setReschedId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reschedule"); }
  };

  if (items.length === 0) return null;
  return (
    <div className="mb-8" data-testid="scheduled-campaigns-section">
      <h3 className="font-serif text-lg text-brand-text flex items-center gap-2 mb-3">
        <CalendarClock size={16} className="text-brand-gold" /> Scheduled Campaigns
      </h3>
      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.id} data-testid={`scheduled-campaign-${c.id}`} className="border border-brand-border bg-brand-surface p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <p className="font-sans text-sm text-brand-text font-medium">{c.campaign_name}</p>
              <p className="font-sans text-xs text-brand-text-muted truncate">{c.subject}</p>
            </div>
            <div className="text-right">
              <p className="font-sans text-[10px] uppercase tracking-wider text-brand-text-muted">Sends at</p>
              <p data-testid={`scheduled-time-${c.id}`} className="font-sans text-sm text-brand-gold font-semibold whitespace-nowrap">{new Date(c.scheduled_for).toLocaleString()}</p>
            </div>
            <span className={`font-sans text-[10px] uppercase tracking-wider px-2 py-1 border whitespace-nowrap ${STATUS_STYLES[c.status] || STATUS_STYLES.cancelled}`}>
              {STATUS_LABELS[c.status] || c.status}
            </span>
            {c.status === "scheduled" && (
              reschedId === c.id ? (
                <div className="flex items-center gap-2">
                  <input data-testid="reschedule-input" type="datetime-local" value={reschedWhen} onChange={(e) => setReschedWhen(e.target.value)} className="bg-brand-bg border border-brand-border text-brand-text font-sans text-xs px-2 py-1.5 focus:border-brand-gold outline-none" />
                  <button data-testid="reschedule-save" onClick={() => saveReschedule(c)} className="px-3 py-1.5 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors">Save</button>
                  <button onClick={() => setReschedId(null)} className="p-1.5 text-brand-text-muted hover:text-brand-text"><X size={14} /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button data-testid={`reschedule-btn-${c.id}`} onClick={() => { setReschedId(c.id); setReschedWhen(""); }} className="flex items-center gap-1.5 px-3 py-1.5 border border-brand-border text-brand-text-muted font-sans text-xs hover:border-brand-gold hover:text-brand-gold transition-colors"><CalendarClock size={12} /> Reschedule</button>
                  <button data-testid={`cancel-scheduled-${c.id}`} onClick={() => cancel(c)} className="flex items-center gap-1.5 px-3 py-1.5 border border-red-500/40 text-red-400 font-sans text-xs hover:bg-red-500/10 transition-colors"><Trash2 size={12} /> Cancel</button>
                </div>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const num = (v) => (v === null || v === undefined ? "—" : v);

const CampaignDetail = ({ campaignId, onClose }) => {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/campaigns/${campaignId}`, authHeaders())
      .then((r) => setDetail(r.data))
      .catch(() => onClose());
  }, [campaignId, onClose]);

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" data-testid="campaign-detail-modal">
      <div className="bg-brand-surface border border-brand-gold/50 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-brand-border">
          <h3 className="font-serif text-lg text-brand-text">{detail?.campaign_name || detail?.subject || "Campaign"}</h3>
          <button data-testid="campaign-detail-close" onClick={onClose} className="p-1.5 text-brand-text-muted hover:text-brand-text"><X size={16} /></button>
        </div>
        {!detail ? (
          <p className="font-sans text-sm text-brand-text-muted p-8 text-center">Loading…</p>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-brand-border/50 space-y-1">
              <p className="font-sans text-xs text-brand-text-muted">Sent: <span className="text-brand-text">{new Date(detail.created_at).toLocaleString()}</span></p>
              <p className="font-sans text-xs text-brand-text-muted">Subject: <span className="text-brand-gold">{detail.subject}</span></p>
              <p className="font-sans text-xs text-brand-text-muted">Delivered: <span className="text-brand-text">{detail.sent} of {detail.total}</span>{detail.failed > 0 && <span className="text-red-400"> · {detail.failed} failed</span>}</p>
            </div>
            <div className="flex-1 overflow-auto bg-black/40 p-4 flex justify-center">
              {detail.html_preview ? (
                <iframe title="Sent email" data-testid="campaign-email-iframe" srcDoc={`<body style="margin:0;background:#000;">${detail.html_preview}</body>`} sandbox="" style={{ width: 620, height: 520, border: "1px solid #2C241D", background: "#000" }} />
              ) : (
                <div className="font-sans text-sm text-brand-text-muted p-6 whitespace-pre-line" data-testid="campaign-text-fallback">
                  <p className="text-brand-text mb-2">Exact HTML wasn't saved for this older campaign. Message text:</p>
                  {detail.message}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const CampaignsTab = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/campaigns`, authHeaders())
      .then((r) => setCampaigns(r.data))
      .catch((e) => console.error("Failed to load campaigns:", e));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="campaigns-tab">
      <h2 className="font-serif text-2xl text-brand-text flex items-center gap-2 mb-2">
        <History size={20} className="text-brand-gold" /> Campaign History
      </h2>
      <p className="font-sans text-xs text-brand-text-muted mb-6">
        Every promotional email sent, with opens, clicks and bookings when tracking is available. Click a row to review the exact email.
      </p>

      <ScheduledSection />

      {campaigns.length === 0 ? (
        <p data-testid="campaigns-empty" className="font-sans text-sm text-brand-text-muted border border-brand-border bg-brand-surface p-8 text-center">
          No campaigns sent yet. Your first Client Blast will appear here with full performance stats.
        </p>
      ) : (
        <div className="overflow-x-auto border border-brand-border">
          <table className="w-full text-left" data-testid="campaigns-table">
            <thead>
              <tr className="border-b border-brand-border bg-brand-surface">
                {["Campaign", "Date Sent", "Subject", "Recipients",
                  <span key="o" className="inline-flex items-center gap-1"><MailOpen size={11} /> Opens</span>, "Open Rate",
                  <span key="c" className="inline-flex items-center gap-1"><MousePointerClick size={11} /> Clicks</span>, "CTR",
                  <span key="b" className="inline-flex items-center gap-1"><CalendarCheck size={11} /> Bookings</span>].map((h, i) => (
                  <th key={i} className="font-sans text-[10px] uppercase tracking-wider text-brand-text-muted px-3 py-2.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c, idx) => (
                <tr
                  key={c.id || idx}
                  data-testid={`campaign-row-${c.id || idx}`}
                  onClick={() => c.id && setOpenId(c.id)}
                  className={`border-b border-brand-border/40 ${c.id ? "cursor-pointer hover:bg-brand-gold/5" : "opacity-60"} transition-colors`}
                >
                  <td className="px-3 py-3 font-sans text-sm text-brand-text max-w-[180px] truncate">{c.campaign_name}</td>
                  <td className="px-3 py-3 font-sans text-xs text-brand-text-muted whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-3 font-sans text-xs text-brand-text-muted max-w-[200px] truncate">{c.subject}</td>
                  <td className="px-3 py-3 font-sans text-sm text-brand-text">{c.recipients}</td>
                  <td className="px-3 py-3 font-sans text-sm text-brand-gold">{num(c.opens)}</td>
                  <td className="px-3 py-3 font-sans text-sm text-brand-gold">{pct(c.open_rate)}</td>
                  <td className="px-3 py-3 font-sans text-sm text-brand-gold">{num(c.clicks)}</td>
                  <td className="px-3 py-3 font-sans text-sm text-brand-gold">{pct(c.ctr)}</td>
                  <td className="px-3 py-3 font-sans text-sm text-green-400">{num(c.bookings)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="font-sans text-[10px] text-brand-text-muted px-3 py-2">"—" means tracking wasn't available for that send (older campaigns).</p>
        </div>
      )}
      {openId && <CampaignDetail campaignId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
};

export default CampaignsTab;
